import { Box, Divider, Flex, List } from '@mantine/core'

import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import classes from './TermsConditions.module.css'

export function Component() {
  return (
    <Box className={classes.container}>
      <Flex className={classes.header}>
        <Title variant="h1">Terms &amp; Conditions for Rolan Wealth</Title>

        <Text variant="body_1_grey">Last Updated: October 29, 2026</Text>
      </Flex>

      <Text>
        Welcome to Rolan Wealth (&ldquo;Rolan,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;). These Terms &amp; Conditions
        (&ldquo;Terms&rdquo;) govern your access to and use of our platform,
        tools, and services (collectively, the &ldquo;Services&rdquo;), which
        are provided by CLZ Ventures LLC, doing business as Rolan Wealth. By
        accessing or using our Services, you agree to be bound by these Terms.
        If you do not agree, you must discontinue using our Services
        immediately.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">1. Definitions and Scope</Title>

      <Title variant="h3">1.1 Definitions</Title>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>&ldquo;Platform&rdquo;</strong> refers to the Rolan Wealth
            website, web application, and any associated digital tools, whether
            accessed via browser, mobile device, or third-party integration,
            including any features currently in pilot, beta, or pre-release
            status.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Services&rdquo;</strong> refers to the financial
            coaching, financial wellness education, and related tools and
            resources provided by Rolan Wealth through the Platform, including
            budgeting tools, net worth tracking, goal-setting tools, AI-Powered
            Insights, and facilitated access to Certified Financial
            Planner&trade; (CFP®) professionals for financial coaching and
            education. The Services do not include investment advice, legal
            advice, or tax advice of any kind.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;AI-Powered Insights&rdquo;</strong> means the
            automated, rule-based observations, notifications, and informational
            summaries generated by the Platform, which may be powered in whole
            or in part by artificial intelligence or machine learning
            technologies. AI-Powered Insights are automated outputs that are not
            tailored to your specific financial situation and do not constitute
            advice of any kind.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Beta/Pilot Services&rdquo;</strong> means any
            feature, function, module, or version of the Platform or Services
            offered on a pre-release, pilot, beta, or limited-availability
            basis, and identified as such within the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              &ldquo;User,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;
            </strong>{' '}
            refers to any individual or entity accessing or using the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Content&rdquo;</strong> includes all text, data,
            information, images, AI-Powered Insights, and other materials
            accessible through the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Third-Party Services&rdquo;</strong> refers to tools,
            integrations, or content provided by entities other than Rolan
            Wealth, including but not limited to Plaid (financial account
            aggregation) Calendly (scheduling), and our third-party payment
            processor.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Connected Account Data&rdquo;</strong> means
            financial account information, transaction data, balances, and
            related data retrieved from third-party financial institutions or
            data aggregators such as Plaid.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Employer Agreement&rdquo;</strong> means the written
            agreement between Rolan Wealth and an employer governing the
            employer&rsquo;s sponsorship of access to the Services for its
            employees, including any services agreement, pilot services
            agreement, or amendment to either.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;CFP® View Access&rdquo;</strong> means the limited,
            read-only permission a User may grant to a CFP® professional to
            view the User&rsquo;s dashboard, budget page, action plan pages, My
            Accounts page, and onboarding questionnaire responses within the
            Platform, as described in Section 5B.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;CFP® Session&rdquo;</strong> means a one-on-one
            consultation between you and a CFP® professional, by video or
            another format, arranged through the Platform, together with any
            follow-up messages exchanged with that CFP® professional through
            the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Guest&rdquo;</strong> means a spouse or partner who
            attends a CFP® Session at your invitation, where permitted under
            your Program, as described in Section 5B.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>&ldquo;Program&rdquo;</strong> means an employer-sponsored
            financial wellness program through which you access the Services
            under an Employer Agreement. The specific terms of your Program are
            its &ldquo;Program Details,&rdquo; as described in Section 7.3.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        1.2 Scope
      </Title>

      <Text>
        These Terms apply to all users of the Services, including those
        accessing the Services via third-party integrations or employer-provided
        platforms. These Terms, together with the Privacy Policy incorporated
        herein by reference and your Program Details as described in Section
        7.3, constitute the entire agreement between you and Rolan Wealth
        regarding the Services. These Terms also apply to any Guest who attends
        a CFP® Session, as described in Section 5B.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">2. Agreement to Terms</Title>

      <Text>
        By accessing our Services, you confirm that you have read, understood,
        and agree to these Terms. Supplemental terms or additional agreements
        may apply to specific features of our Services and are incorporated
        herein by reference. We may modify these Terms from time to time. If we
        make a material change, we will notify you by email to the address
        associated with your Account or through a notice on the Platform, and
        the change will take effect thirty (30) days after that notice, unless
        applicable law requires it to take effect sooner. Other changes take
        effect when the revised Terms are posted. Changes to Section 17 will not
        apply to any dispute of which Rolan Wealth had notice before the change
        took effect. Your continued use of the Services after a change takes
        effect constitutes acceptance of the modified Terms. It is your
        responsibility to review these Terms periodically. If you do not agree
        to any updated Terms, you must stop using the Services.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">3. Company Information</Title>

      <Text>
        CLZ Ventures LLC (dba Rolan Wealth) operates under the jurisdiction of
        the State of New York. Our principal office is located at:{' '}
        <strong>
          54 State Street, Ste 804 #7100, Albany, New York 12207, USA.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">4. Services Offered</Title>

      <Text>
        Rolan Wealth provides a financial coaching and education platform which
        may include one or more of the following:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Personalized financial education content to help users understand
            key financial concepts and strategies.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Goal-setting tools to help users plan for life events such as
            retirement, buying a home, or paying down debt.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Budgeting tools to assist users in tracking and managing their
            expenses.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Net worth tracking tools to provide a consolidated view of assets
            and liabilities.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            AI-Powered Insights: automated, observational outputs that surface
            spending trends, budget variances, and other financial wellness
            observations (see Section 5A).
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        Rolan Wealth also facilitates access to CERTIFIED FINANCIAL
        PLANNER&trade; (CFP®) professionals for educational financial planning
        and guidance conversations (&ldquo;CFP® Sessions&rdquo;), as described
        in Section 5B. The Services are offered as an employee benefit through
        employers and are limited to users located in the United States.
      </Text>

      <Text mt={20}>
        <strong>Rolan Wealth does not:</strong>
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Provide investment advice, legal advice, or tax advice of any kind.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Act as a fiduciary, broker-dealer, or registered investment adviser
            (RIA).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Process or facilitate transactions in your financial accounts.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Make personalized recommendations to buy, sell, or hold any
            securities or financial instruments.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        The Services are designed for financial coaching, education, and
        informational purposes only, enabling users to build financial awareness
        and make better-informed decisions. Users acknowledge and agree that the
        Services do not replace professional advice tailored to their specific
        needs. Any reliance on tools, educational materials, coaching, or
        guidance provided through the Services is at the user&rsquo;s discretion
        and risk.
      </Text>

      <Text mt={20}>
        <strong>Changes to the Services.</strong> We continuously improve the
        Platform and Services and may update, add, modify, or remove features
        from time to time, including during a Program. Features available at one
        time may not remain available, and the Platform may occasionally contain
        bugs or errors or be temporarily unavailable, including for maintenance.
        The core features included in your Program are described in the
        agreement between Rolan Wealth and your employer.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        5A. AI-Powered Insights: Informational Observations Only
      </Title>

      <Text>
        The Platform generates automated informational observations,
        notifications, and summaries (&ldquo;AI-Powered Insights&rdquo;) based
        on rule-based logic and, in some cases, artificial intelligence or
        machine learning technologies. AI-Powered Insights are automated outputs
        that are not tailored to your specific financial situation. For details
        on how AI-Powered Insights are generated using your data, please see our
        Privacy Policy.
      </Text>

      <Text mt={20}>
        <strong>
          AI-POWERED INSIGHTS ARE INFORMATIONAL AND OBSERVATIONAL IN NATURE
          ONLY. THEY DO NOT CONSTITUTE INVESTMENT ADVICE, FINANCIAL ADVICE, TAX
          ADVICE, LEGAL ADVICE, OR ANY FORM OF PROFESSIONAL RECOMMENDATION.
          AI-POWERED INSIGHTS ARE NOT PRESCRIPTIVE AND SHOULD NOT BE CONSTRUED
          AS DIRECTING, RECOMMENDING, OR SUGGESTING ANY SPECIFIC COURSE OF
          ACTION.
        </strong>
      </Text>

      <Text mt={20}>You expressly acknowledge and agree that:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>
              AI-POWERED INSIGHTS ARE GENERATED AUTOMATICALLY AND MAY BE
              INACCURATE, INCOMPLETE, OUTDATED, OR INAPPLICABLE TO YOUR
              INDIVIDUAL FINANCIAL CIRCUMSTANCES.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              AI-POWERED INSIGHTS ARE DERIVED IN PART FROM CONNECTED ACCOUNT
              DATA PROVIDED BY THIRD PARTIES SUCH AS PLAID, WHICH MAY ITSELF BE
              INACCURATE, INCOMPLETE, OR DELAYED.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              THE UNDERLYING RULES AND MODELS USED TO GENERATE AI-POWERED
              INSIGHTS MAY CONTAIN ERRORS OR LIMITATIONS, AND ROLAN WEALTH DOES
              NOT GUARANTEE THEIR ACCURACY, RELIABILITY, OR SUITABILITY FOR ANY
              PURPOSE.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              AI-POWERED INSIGHTS ARE NOT A SUBSTITUTE FOR INDEPENDENT FINANCIAL
              ANALYSIS OR ADVICE FROM A QUALIFIED, LICENSED PROFESSIONAL.
            </strong>
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ROLAN WEALTH SHALL
          HAVE NO LIABILITY WHATSOEVER FOR ANY LOSS, DAMAGE, HARM, OR ADVERSE
          FINANCIAL OUTCOME ARISING FROM YOUR USE OF, RELIANCE ON, OR
          MISINTERPRETATION OF ANY AI-POWERED INSIGHT.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        5B. CFP® Sessions, CFP® Professionals, and CFP® View Access
      </Title>

      <Title variant="h3">Scope of CFP® Sessions</Title>

      <Text>
        CFP® Sessions are educational financial planning and guidance
        conversations on topics you choose, which may include budgeting, cash
        flow, debt management, saving goals, retirement readiness, employer
        benefits, and how equity compensation works. Through the Services, CFP®
        professionals will not:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>Recommend specific securities or investment products;</Text>
        </List.Item>

        <List.Item>
          <Text>Manage, or take custody of, your assets;</Text>
        </List.Item>

        <List.Item>
          <Text>Sell, or receive commissions on, any financial product;</Text>
        </List.Item>

        <List.Item>
          <Text>Prepare tax returns;</Text>
        </List.Item>

        <List.Item>
          <Text>Prepare legal documents such as wills or trusts;</Text>
        </List.Item>

        <List.Item>
          <Text>
            Recommend whether or when you should exercise, sell, or hold any
            equity or equity awards granted by your employer; or
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Make individualized recommendations regarding the selection or
            allocation of investments within any retirement plan sponsored by
            your employer, or regarding rollovers or distributions from such a
            plan.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        Rolan Wealth is not a registered investment adviser, and the Services do
        not constitute investment advisory services. Rolan Wealth does not act
        as a fiduciary to any employee benefit plan sponsored by your employer.
        You acknowledge and agree that:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            You retain full and exclusive responsibility for all financial
            decisions you make, regardless of any coaching, education, or
            general guidance received during a CFP® Session.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        CFP® Professionals
      </Title>

      <Text>
        CFP® Sessions are provided by CFP® professionals whom Rolan Wealth
        engages as independent contractors. Rolan Wealth selects each CFP®
        professional, verifies that they hold a current CFP® certification in
        good standing, and requires them to follow the scope of CFP® Sessions
        described in this Section and to keep your information confidential.
        Each CFP® professional exercises their own independent professional
        judgment during CFP® Sessions. Some CFP® professionals also operate
        their own advisory practices. A CFP® Session through Rolan Wealth is
        not an engagement with that practice, and CFP® professionals may not
        use your information to solicit you for any separate business.
      </Text>

      <Text mt={8}>
        <strong>Responsibility.</strong> Rolan Wealth is responsible for its own
        obligations under these Terms, including selecting and verifying CFP®
        professionals and protecting your information. To the fullest extent
        permitted by law, Rolan Wealth is not responsible for the independent
        professional judgment of a CFP® professional or for any financial
        decision you make, and any liability of Rolan Wealth is subject to
        Sections 13 and 14. Nothing in these Terms limits liability for Rolan
        Wealth&rsquo;s gross negligence, willful misconduct, or fraud, or any
        liability that cannot be limited under applicable law.
      </Text>

      <Title mt={20} variant="h3">
        Spouses and Partners
      </Title>

      <Text>
        Where your Program permits it, you may invite your spouse or partner (a
        &ldquo;Guest&rdquo;) to attend your CFP® Sessions, subject to any
        conditions in your Program Details (see Section 7.3). A Guest does not
        receive a Platform account and may not use your account credentials. By
        inviting a Guest, you (a) consent to the CFP® professional discussing
        your financial information in your Guest&rsquo;s presence; (b) agree to
        tell your Guest that the session is educational only and subject to the
        limitations in these Terms; and (c) agree that Rolan Wealth is not
        responsible for any use or disclosure of your information by your Guest.
        A Guest&rsquo;s attendance is subject to Sections 5B, 13, 14, 15, and 17
        of these Terms, and Rolan Wealth may ask a Guest to confirm their
        agreement to these Terms before or at the start of a CFP® Session. The
        CFP® professional may decline to conduct, or may end, a CFP® Session
        in a Guest&rsquo;s presence if Guests are not permitted under your
        Program or the Guest does not agree to these Terms. Unless your Program
        Details provide otherwise, a CFP® Session attended by a Guest counts as
        a single CFP® Session.
      </Text>

      <Title mt={20} variant="h3">
        CFP® Session Availability
      </Title>

      <Text>
        The number of CFP® Sessions available through a Program, and whether
        they are allocated to individual participants or shared among them, are
        set by your Program Details. CFP® Sessions may be limited in number,
        are subject to CFP® professional availability, and are not guaranteed.
        Consistent with your Program Details, Rolan Wealth may limit the number
        of CFP® Sessions any participant may book, and may make additional
        sessions available from time to time. Unless your Program Details or the
        booking terms shown to you provide otherwise, a CFP® Session cancelled
        less than twenty-four (24) hours in advance, or missed, counts as a used
        session.
      </Text>

      <Title mt={20} variant="h3">
        CFP® Sessions You Purchase
      </Title>

      <Text>
        Where available, you may purchase additional CFP® Sessions directly
        from Rolan Wealth. The price and any cancellation and refund terms will
        be shown to you before you complete your purchase, and they apply to
        that purchase together with these Terms. Payments are processed by our
        third-party payment processor. Your employer is not responsible for, and
        is not informed of, CFP® Sessions you purchase.
      </Text>

      <Title mt={20} variant="h3">
        Messaging with CFP® Professionals
      </Title>

      <Text>
        The Platform may allow you to exchange messages with your CFP®
        professional to follow up on a CFP® Session. Messages are not monitored
        in real time, are not intended for urgent or time-sensitive matters, and
        are subject to the same scope and limitations as CFP® Sessions.
      </Text>

      <Title mt={20} variant="h3">
        CFP® View Access: User-Granted Read-Only Permission
      </Title>

      <Text>
        To facilitate more informed CFP® coaching sessions, the Platform
        provides a mechanism by which you may, at your sole discretion, grant a
        CFP® professional limited, read-only access to certain areas of your
        Platform account (&ldquo;CFP® View Access&rdquo;). If granted, the
        CFP® professional will have read-only visibility into your dashboard,
        budget page, action plan pages, My Accounts page, and onboarding
        questionnaire responses. For details on how this data is handled, please
        see our Privacy Policy.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>View Only, No Edit Rights:</strong> CFP® View Access is
            strictly read-only. The CFP® professional has no ability to edit,
            modify, create, or delete any data within your account.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>No Access to Financial Accounts:</strong> CFP® View Access
            does not provide access to your linked financial account
            credentials, raw bank login data, or any ability to initiate
            transactions or interact with your financial institutions in any
            way.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>User Control:</strong> You may revoke CFP® View Access at
            any time through your account settings, at which point access is
            terminated immediately. Granting or revoking CFP® View Access is
            entirely your choice and is not a condition of using any other
            feature of the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Your Consent and Responsibility:</strong> By granting CFP®
            View Access, you expressly consent to the CFP® professional viewing
            the designated areas of your account. You are solely responsible for
            the decision to grant this access and for any consequences thereof.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>No Liability for Use of Viewed Information:</strong> To the
            maximum extent permitted by law, Rolan Wealth shall have no
            liability for any harm arising from: (a) your decision to grant
            CFP® View Access; (b) the CFP® professional&rsquo;s use of
            information viewed during access; or (c) any action by a CFP®
            professional that exceeds the intended read-only scope of the access
            granted.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>No Change to Scope or Relationship:</strong> Granting CFP®
            View Access does not change the scope of CFP® Sessions described in
            this Section and does not create any fiduciary, advisory, or other
            professional relationship between you and Rolan Wealth.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        Recording Policy
      </Title>

      <Text>
        CFP® Sessions conducted through the Platform are not recorded by Rolan
        Wealth unless expressly disclosed in writing at the time of scheduling.
        If sessions are ever recorded in the future, Rolan Wealth will obtain
        your prior written consent before doing so. Live group events, such as
        launch Q&amp;A sessions and webinars, may be recorded and made available
        to other Program participants or eligible employees. If you attend a
        recorded event, your name, image, voice, and any questions you ask
        during the event may appear in the recording.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">5C. Beta and Early-Access Features</Title>

      <Text>
        <strong>
          IMPORTANT: CERTAIN FEATURES OF THE ROLAN WEALTH PLATFORM MAY BE
          OFFERED ON A PILOT AND/OR BETA BASIS AND WILL BE IDENTIFIED AS SUCH
          WITHIN THE PLATFORM. SUCH FEATURES MAY CONTAIN BUGS, ERRORS,
          INCOMPLETE FUNCTIONALITY, INACCURATE DATA, OR OTHER DEFECTS. YOUR USE
          OF ANY BETA OR PILOT FEATURE IS ENTIRELY AT YOUR OWN RISK.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        No Warranties for Beta/Pilot Services
      </Title>

      <Text>
        <strong>
          BETA/PILOT SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND. ROLAN WEALTH
          EXPRESSLY DISCLAIMS ALL WARRANTIES WITH RESPECT TO BETA/PILOT
          SERVICES, INCLUDING BUT NOT LIMITED TO: (A) THE ACCURACY,
          COMPLETENESS, RELIABILITY, OR TIMELINESS OF ANY DATA OR OUTPUTS; (B)
          THE ABSENCE OF BUGS, ERRORS, OR DEFECTS; (C) UNINTERRUPTED OR
          ERROR-FREE OPERATION; AND (D) FITNESS FOR ANY PARTICULAR PURPOSE.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        No Obligation to Support or Continue
      </Title>

      <Text>
        Rolan Wealth has no obligation to provide technical support for
        Beta/Pilot Services and may discontinue, modify, or remove any
        Beta/Pilot Service at any time in its sole discretion, with or without
        notice, and without liability to you. This does not change any
        commitment Rolan Wealth has made to your employer in its agreement with
        Rolan Wealth.
      </Text>

      <Title mt={20} variant="h3">
        Assumption of Risk for Beta/Pilot Services
      </Title>

      <Text>
        <strong>
          YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOUR USE OF BETA/PILOT
          SERVICES IS AT YOUR SOLE RISK. ROLAN WEALTH SHALL HAVE NO LIABILITY
          WHATSOEVER FOR ANY LOSS, DAMAGE, INACCURACY, OR OTHER HARM ARISING
          FROM YOUR USE OF OR RELIANCE ON BETA/PILOT SERVICES, REGARDLESS OF THE
          THEORY OF LIABILITY AND EVEN IF ROLAN WEALTH HAS BEEN ADVISED OF THE
          POSSIBILITY OF SUCH HARM.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        Feedback on Beta/Pilot Services
      </Title>

      <Text>
        If you provide feedback about Beta/Pilot Services, you agree that Rolan
        Wealth owns all such feedback and may use it for any purpose without
        compensation to you or any obligation of confidentiality.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">6. Eligibility &amp; Right to Use</Title>

      <Title variant="h3">6.1 Eligibility</Title>

      <Text>
        You must be at least 18 years of age to use the Platform and Services
        provided by Rolan Wealth. By accessing or using the Platform, you
        represent and warrant that:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            You are at least 18 years old and possess the legal capacity to form
            a binding contract under applicable law.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            You are not a competitor of Rolan Wealth or seeking to use the
            Platform to build a competitive product or service.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            You will comply with all applicable statutes, orders, regulations,
            rules, and other laws while using the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            You will provide accurate and up-to-date information as required for
            using the Platform and Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            You are solely responsible for maintaining the confidentiality and
            security of your account credentials. You agree not to share,
            disclose, or transfer your credentials to any third party, including
            a spouse or partner. Rolan Wealth shall have no liability for any
            loss or damage arising from unauthorized access to your account
            resulting from your failure to safeguard your credentials.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        If you do not meet these criteria, you may not access or use the
        Services.
      </Text>

      <Title mt={20} variant="h3">
        6.2 Linking
      </Title>

      <Text>
        You may create text hyperlinks to the Platform for informational
        purposes, provided such links do not portray Rolan Wealth in a false,
        misleading, derogatory, or defamatory manner. The linking website must
        comply with applicable laws and must not contain objectionable material
        as determined by Rolan Wealth.
      </Text>

      <Text mt={8}>
        Public search engines are permitted to use spiders to copy content from
        the Platform for publicly available, searchable indices, subject to
        restrictions outlined in our robots.txt file. Rolan Wealth reserves the
        right to revoke these permissions at any time.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">7. User Accounts, Access, Fees, and Payments</Title>

      <Title variant="h3">7.1 Account Creation and Security</Title>

      <Text>
        To access certain features of the Services, you must create an account
        (&ldquo;Account&rdquo;). You agree to:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Provide accurate, current, and complete information during
            registration.
          </Text>
        </List.Item>

        <List.Item>
          <Text>Maintain the confidentiality of your account credentials.</Text>
        </List.Item>

        <List.Item>
          <Text>
            Promptly update any information to ensure it remains accurate and
            current.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        You are responsible for all activities conducted under your Account.
        Notify us immediately at support@rolanwealth.com if you suspect
        unauthorized use or any security breach. Rolan Wealth reserves the right
        to suspend or terminate your Account for violations of these Terms.
      </Text>

      <Title mt={20} variant="h3">
        7.2 Employer-Sponsored Access &amp; Fees
      </Title>

      <Text>
        Rolan Wealth provides its Services to employees through
        employer-sponsored programs. All fees for access to Rolan Wealth&rsquo;s
        Services are determined through direct agreements with employers
        (&ldquo;Employer Agreements&rdquo;). Employees using the Services as
        part of an employer-sponsored program are not required to pay any
        subscription fees directly to Rolan Wealth. The only exception is any
        additional CFP® Session you choose to purchase directly, as described
        in Section 5B.
      </Text>

      <Title mt={20} variant="h3">
        7.3 Enrollment, Eligibility, and Access
      </Title>

      <Text>
        Your access to the Services is subject to the Employer Agreement between
        Rolan Wealth and your employer. You are not a party to, or a third-party
        beneficiary of, the Employer Agreement.
      </Text>

      <Text mt={8}>
        <strong>Program Details.</strong> Programs differ by employer. The terms
        of your Program, including eligibility requirements, enrollment periods
        and deadlines, the number of participants, how places are allocated and
        how any waitlist operates, the number of CFP® Sessions available and
        whether they are shared among participants, whether a spouse or partner
        may attend CFP® Sessions, scheduling and cancellation rules, and the
        Program term (together, &ldquo;Program Details&rdquo;), are set by the
        Employer Agreement and will be described to you at enrollment, in
        Program communications, or within the Platform. Program Details may
        change during a Program as permitted by the Employer Agreement. If
        Program Details differ from the general description of those matters in
        these Terms, the Program Details apply. Program Details do not otherwise
        modify these Terms, including the scope of CFP® Sessions described in
        Section 5B and Sections 13, 14, 16, and 17.
      </Text>

      <Text mt={8}>
        Enrolling in a Program does not guarantee a place. If more eligible
        employees enroll than a Program can accommodate, places may be allocated
        in the order enrollments are received, by random selection, or by
        another method described in your Program Details, and other eligible
        employees may be placed on a waitlist. Your access to the Services may
        end if:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>Your employment with the sponsoring employer ends.</Text>
        </List.Item>

        <List.Item>
          <Text>The Employer Agreement terminates or expires.</Text>
        </List.Item>

        <List.Item>
          <Text>
            You no longer meet eligibility requirements as established by your
            employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>You withdraw from the Program.</Text>
        </List.Item>

        <List.Item>
          <Text>The Program term ends and is not renewed.</Text>
        </List.Item>

        <List.Item>
          <Text>
            You do not activate your Account within the period described in your
            Program Details, other eligible employees are waiting for a place,
            and you have not activated your Account after a reminder.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        Upon termination of your access, your personal data will be retained and
        deleted in accordance with our Privacy Policy. Employees with questions
        regarding their access should contact their employer or reach out to
        Rolan Wealth at support@rolanwealth.com.
      </Text>

      <Title mt={20} variant="h3">
        7.4 Payment Responsibility of Employers
      </Title>

      <Text>
        All financial obligations, including subscription fees for the Services,
        are the responsibility of the sponsoring employer under the Employer
        Agreement. Payment terms, renewal conditions, and any applicable fees or
        penalties are governed by the Employer Agreement and are not covered
        under these Terms. This Section does not apply to CFP® Sessions you
        purchase directly, as described in Section 5B.
      </Text>

      <Title mt={20} variant="h3">
        7.5 Program Communications
      </Title>

      <Text>
        By enrolling in a Program, you agree that Rolan Wealth may contact you
        about the Program, including welcome messages, scheduling, reminders,
        and program updates, by email and, where your employer permits, through
        your employer&rsquo;s workplace messaging platform. Rolan Wealth will
        not discuss your personal financial matters through your
        employer&rsquo;s workplace messaging platform, and messages sent there
        may be accessible to your employer under its own policies. You may opt
        out of non-essential communications at any time.
      </Text>

      <Title mt={20} variant="h3">
        7.6 Tax Treatment of Your Benefit
      </Title>

      <Text>
        Your employer, not Rolan Wealth, determines the tax treatment of any
        benefit provided through a Program, including whether any amount is
        taxable income to you. Rolan Wealth does not advise on the tax treatment
        of the benefit. Please direct any questions about it to your employer.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">8. Acceptable Use and Prohibited Actions</Title>

      <Text>
        You agree to use the Services only for lawful purposes and in accordance
        with these Terms. By accessing or using the Services, you agree not to
        engage in the following prohibited activities:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Unlawful or Unauthorized Use:</strong> Using the Services
            for any unlawful, fraudulent, or unauthorized purpose; violating any
            applicable laws, regulations, or these Terms.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Security Violations:</strong> Attempting to gain
            unauthorized access to data, accounts, or systems; introducing
            viruses, malware, or harmful code; interfering with the operation or
            performance of the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Misuse of Content and Intellectual Property:</strong>{' '}
            Uploading defamatory, obscene, abusive, or threatening content;
            violating intellectual property rights; reverse engineering or
            decompiling the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Misrepresentation and Unauthorized Collection:</strong>{' '}
            Impersonating any individual or entity; collecting personally
            identifiable information of other users without consent.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Commercial Exploitation and Automated Tools:</strong>{' '}
            Exploiting the Services for commercial purposes without prior
            authorization; using automated tools, bots, or data extraction
            methods to scrape or collect information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Circumventing Access Controls:</strong> Attempting to
            access, alter, or interfere with CFP® View Access controls, or
            attempting to gain access beyond the read-only scope described in
            Section 5B.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        Rolan Wealth reserves the right to suspend, terminate, or restrict your
        access to the Platform for any violation of these Terms, and to take
        legal action to protect Rolan Wealth&rsquo;s interests and the safety of
        its users.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">9. Intellectual Property</Title>

      <Title variant="h3">9.1 Intellectual Property Ownership</Title>

      <Text>
        The Services, including the website, web application, and any associated
        platforms, are owned and operated by Rolan Wealth. All content,
        features, functionality, and materials made available through the
        Services, including but not limited to text, visual interfaces,
        information, graphics, designs, compilations, computer code, products,
        software, trade names, trademarks, service marks, logos, images, and
        other proprietary content (collectively, the &ldquo;Rolan Wealth
        Materials&rdquo;), are the exclusive property of Rolan Wealth or its
        licensors and are protected by U.S. and international copyright,
        trademark, and other intellectual property laws.
      </Text>

      <Title mt={20} variant="h3">
        9.2 Prohibited Use of Intellectual Property
      </Title>

      <Text>Users may not:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        type="ordered"
        withPadding
      >
        <List.Item>
          <Text>
            Copy, reproduce, modify, or distribute the Rolan Wealth Materials
            without prior written consent.
          </Text>
        </List.Item>

        <List.Item>
          <Text>Use trademarks or logos without express authorization.</Text>
        </List.Item>

        <List.Item>
          <Text>
            Frame, mirror, or display Rolan Wealth Materials without permission.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        9.3 Feedback and Contributions
      </Title>

      <Text>
        By submitting feedback or suggestions, you grant Rolan Wealth a
        perpetual, royalty-free license to use and incorporate such suggestions
        into our Services.
      </Text>

      <Title mt={20} variant="h3">
        9.4 Reservation of Rights
      </Title>

      <Text>
        All rights not expressly granted in these Terms are reserved by Rolan
        Wealth and its licensors.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        10. Third-Party Services and Connected Account Data
      </Title>

      <Text>
        The Services integrate with Third-Party Services including Plaid for
        financial account data aggregation and Calendly for scheduling. Rolan
        Wealth does not control and is not responsible for the acts, omissions,
        data accuracy, availability, or services of any third party. Your use of
        Third-Party Services is governed by their respective terms and privacy
        policies, which you are solely responsible for reviewing.
      </Text>

      <Text mt={20}>
        <strong>
          ROLAN WEALTH MAKES NO REPRESENTATION OR WARRANTY REGARDING THE
          ACCURACY, COMPLETENESS, TIMELINESS, RELIABILITY, OR AVAILABILITY OF
          ANY CONNECTED ACCOUNT DATA RETRIEVED THROUGH THIRD-PARTY DATA
          AGGREGATORS, INCLUDING PLAID, OR DIRECTLY FROM FINANCIAL INSTITUTIONS.
          WITHOUT LIMITING THE FOREGOING:
        </strong>
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>
              TRANSACTIONS MAY BE MISSING, DUPLICATED, DELAYED, MISLABELED, OR
              INCORRECTLY CATEGORIZED DUE TO LIMITATIONS OF THIRD-PARTY DATA
              PROVIDERS OR FINANCIAL INSTITUTIONS.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              ACCOUNT BALANCES, NET WORTH CALCULATIONS, SPENDING TOTALS, AND
              BUDGET SUMMARIES DISPLAYED ON THE PLATFORM ARE DERIVED FROM
              THIRD-PARTY DATA AND MAY NOT REFLECT YOUR ACTUAL FINANCIAL
              POSITION AT ANY GIVEN TIME.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              CONNECTIVITY BETWEEN THE PLATFORM AND FINANCIAL INSTITUTIONS MAY
              BE INTERRUPTED OR TERMINATED AT ANY TIME WITHOUT NOTICE DUE TO
              ACTIONS BY FINANCIAL INSTITUTIONS, DATA AGGREGATORS, OR OTHER
              THIRD PARTIES OUTSIDE ROLAN WEALTH&rsquo;S CONTROL.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              OLDER OR BACKDATED TRANSACTIONS MAY BE ADDED TO YOUR DATA AT ANY
              TIME BY THIRD-PARTY PROVIDERS, WHICH MAY AFFECT DISPLAYED SPENDING
              TOTALS AND INSIGHTS WITHOUT PRIOR NOTICE.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              ROLAN WEALTH DOES NOT INDEPENDENTLY VERIFY, AUDIT, OR RECONCILE
              ANY CONNECTED ACCOUNT DATA.
            </strong>
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        <strong>
          ANY FINANCIAL FIGURES, SPENDING ANALYSES, BUDGET CALCULATIONS, OR NET
          WORTH ESTIMATES DISPLAYED ON THE PLATFORM ARE FOR INFORMATIONAL AND
          EDUCATIONAL PURPOSES ONLY AND SHOULD NOT BE RELIED UPON AS ACCURATE OR
          CURRENT REPRESENTATIONS OF YOUR FINANCIAL SITUATION. ROLAN WEALTH
          SHALL HAVE NO LIABILITY FOR ANY DECISION YOU TAKE BASED ON CONNECTED
          ACCOUNT DATA OR ANY PLATFORM DISPLAY DERIVED THEREFROM.
        </strong>
      </Text>

      <Text mt={20}>
        These disclaimers apply to all third-party integrations, whether
        currently in use or added in the future.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">11. Data Privacy and Security</Title>

      <Text>
        Your use of the Services is subject to our Privacy Policy, which is
        incorporated into these Terms by reference and governs how we collect,
        use, store, and protect your personal information. Please review our
        Privacy Policy carefully.
      </Text>

      <Text mt={8}>
        Your employer sponsors the Program but does not have access to your
        financial information, your Platform data, or what you discuss with a
        CFP® professional. Your employer receives only your Enrollment
        Information and aggregate, de-identified program reports, as described
        in our Privacy Policy. Our agreements with employers prohibit them from
        using your enrollment or participation in a Program as a factor in any
        employment decision about you.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">12. Limitations of Use</Title>

      <Text>
        You agree to use the Services only for lawful purposes. Prohibited
        activities include but are not limited to:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>Violating applicable laws, regulations, or these Terms.</Text>
        </List.Item>

        <List.Item>
          <Text>Reverse engineering or attempting to derive source code.</Text>
        </List.Item>

        <List.Item>
          <Text>
            Using the Services to misrepresent your identity or affiliation.
          </Text>
        </List.Item>

        <List.Item>
          <Text>Interfering with the performance of the Services.</Text>
        </List.Item>
      </List>

      <Text mt={8}>
        We reserve the right to terminate your access for any misuse or
        violation.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">13. Limitations of Warranties</Title>

      <Text>
        <strong>
          THE SERVICES, INCLUDING OUR PLATFORM, TOOLS, AND ANY ASSOCIATED
          CONTENT OR INFORMATION (INCLUDING AI-POWERED INSIGHTS AND CONNECTED
          ACCOUNT DATA), ARE PROVIDED ON AN &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE&rdquo; BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
          OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
          ROLAN WEALTH AND ITS AFFILIATES, PARTNERS, EMPLOYEES, AGENTS,
          REPRESENTATIVES, OFFICERS, DIRECTORS, LICENSORS, AND SUPPLIERS
          (COLLECTIVELY, THE &ldquo;ROLAN WEALTH PARTIES&rdquo;) DISCLAIM ALL
          WARRANTIES, EXPRESS OR IMPLIED, ARISING BY STATUTE OR OTHERWISE IN LAW
          OR FROM A COURSE OF DEALING OR USAGE OF TRADE, OR OTHERWISE, INCLUDING
          WITHOUT LIMITATION WARRANTIES AS TO (I) THE ACCURACY, RELIABILITY,
          COMPLETENESS, OR CURRENCY OF THE SERVICES, (II) THE CONTINUED
          ACCESSIBILITY OR AVAILABILITY OF THE SERVICES, (III) THE OPERATION OR
          FUNCTIONALITY OF THE SERVICES, AND (IV) THE ABSENCE OF VIRUSES, TROJAN
          HORSES, WORMS, LOGIC BOMBS, OR OTHER HARMFUL COMPONENTS.
        </strong>
      </Text>

      <Text mt={20}>
        <strong>BY USING THE SERVICES, YOU ACKNOWLEDGE THAT:</strong>
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        type="ordered"
        withPadding
      >
        <List.Item>
          <Text>
            <strong>YOUR USE OF THE SERVICES IS AT YOUR OWN RISK.</strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              WE MAKE NO GUARANTEES THAT THE SERVICES WILL BE ERROR-FREE OR
              UNINTERRUPTED.
            </strong>
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              WE MAKE NO REPRESENTATION OR WARRANTY THAT THE SERVICES WILL MEET
              YOUR REQUIREMENTS OR EXPECTATIONS.
            </strong>
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        <strong>
          THIS DISCLAIMER OF WARRANTIES MAY NOT APPLY IN JURISDICTIONS THAT DO
          NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES. IN SUCH CASES, THE
          DISCLAIMER SHALL APPLY TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">14. Limitation of Liability</Title>

      <Text>
        <strong>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE ROLAN WEALTH
          PARTIES SHALL NOT BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY
          INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE
          DAMAGES (INCLUDING DAMAGES FOR LOSS OF PROFITS, REVENUE, BUSINESS,
          GOODWILL, OR DATA), WHETHER ARISING UNDER CONTRACT, TORT (INCLUDING
          NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF THE ROLAN WEALTH
          PARTIES HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </strong>
      </Text>

      <Text mt={20}>
        <strong>
          WITHOUT LIMITING THE FOREGOING, THE ROLAN WEALTH PARTIES SHALL NOT BE
          LIABLE FOR ANY DAMAGES ARISING FROM: (A) YOUR RELIANCE ON ANY
          AI-POWERED INSIGHT, EDUCATIONAL CONTENT, OR CFP® COACHING OR
          GUIDANCE; (B) ANY FINANCIAL DECISIONS OR ACTIONS YOU TAKE OR FAIL TO
          TAKE; (C) INACCURATE, INCOMPLETE, MISSING, OR DELAYED CONNECTED
          ACCOUNT DATA FROM PLAID OR ANY OTHER THIRD-PARTY DATA PROVIDER; (D)
          CONNECTIVITY FAILURES BETWEEN THE PLATFORM AND ANY FINANCIAL
          INSTITUTION OR THIRD-PARTY DATA PROVIDER; OR (E) ANY DEFECT, BUG,
          ERROR, INACCURACY, INTERRUPTION, OR CHANGE AFFECTING THE PLATFORM,
          INCLUDING ANY BETA/PILOT SERVICE.
        </strong>
      </Text>

      <Text mt={20}>
        <strong>
          IN NO EVENT SHALL THE TOTAL AGGREGATE LIABILITY OF THE ROLAN WEALTH
          PARTIES FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR
          YOUR USE OF THE SERVICES EXCEED THE GREATER OF (I) $500 OR (II) THE
          TOTAL AMOUNTS YOU PAID DIRECTLY TO ROLAN WEALTH IN THE TWELVE (12)
          MONTHS BEFORE THE CLAIM AROSE. THE LIMITATIONS OF LIABILITY SET FORTH
          IN THIS SECTION SHALL APPLY REGARDLESS OF THE THEORY OF LIABILITY AND
          SHALL REMAIN IN FULL FORCE AND EFFECT EVEN IF ANY LIMITED REMEDY SET
          FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
        </strong>
      </Text>

      <Text mt={20}>
        <strong>
          THE FOREGOING LIMITATIONS OF LIABILITY SHALL NOT APPLY TO THE EXTENT
          PROHIBITED BY APPLICABLE LAW, AND SOME JURISDICTIONS MAY NOT ALLOW THE
          LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL
          DAMAGES. IN SUCH CASES, THE LIMITATION SHALL APPLY TO THE MAXIMUM
          EXTENT PERMITTED BY LAW. NOTHING IN THESE TERMS LIMITS OR EXCLUDES
          LIABILITY FOR ROLAN WEALTH&rsquo;S GROSS NEGLIGENCE, WILLFUL
          MISCONDUCT, OR FRAUD, OR ANY LIABILITY THAT CANNOT BE LIMITED OR
          EXCLUDED UNDER APPLICABLE LAW.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        15. No Legal, Tax, or Financial Advice; Financial Coaching and Education
        Only
      </Title>

      <Text>
        Rolan Wealth provides financial coaching and education through its
        Services, including facilitated access to Certified Financial
        Planner&trade; (CFP®) professionals. These services are intended for
        financial coaching, education, and informational purposes only. Rolan
        Wealth does not provide legal, tax, investment, or financial advice, and
        no information, tools, coaching, or guidance provided through our
        Services should be construed as such.
      </Text>

      <Text mt={8}>
        The coaching and education offered by Rolan Wealth and through its
        facilitated access to CFP® professionals is not intended to serve as a
        substitute for professional advice tailored to your specific financial,
        legal, or tax circumstances. We encourage you to consult qualified
        professionals, such as licensed financial advisors, tax advisors, or
        attorneys, for advice specific to your situation.
      </Text>

      <Text mt={8}>By using our Services, you acknowledge and agree that:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        type="ordered"
        withPadding
      >
        <List.Item>
          <Text>
            <strong>
              Rolan Wealth is not acting as a fiduciary, broker-dealer, or
              Registered Investment Adviser (RIA).
            </strong>{' '}
            The Services are a financial coaching and education platform only.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              CFP® professionals available through our Services provide
              financial coaching and education only.
            </strong>{' '}
            They are not providing investment advice or recommendations to buy,
            sell, or hold any securities or financial instruments, and work
            within the scope of CFP® Sessions described in Section 5B.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Any coaching or guidance offered is general in nature
            </strong>{' '}
            and not intended to result in specific investment or financial
            planning outcomes tailored to your individual circumstances.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Users remain responsible for their financial decisions.
            </strong>{' '}
            You retain full responsibility for all financial decisions and
            actions taken based on information, coaching, or guidance provided
            through Rolan Wealth&rsquo;s Services or its facilitated CFP®
            professionals.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Rolan Wealth disclaims liability for outcomes.</strong>{' '}
            Rolan Wealth is not responsible for any errors, inaccuracies, or
            omissions in the information or coaching provided, and disclaims
            liability for any losses, damages, or outcomes resulting from
            actions you take or fail to take based on any information or
            coaching received through our Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              The coaching and education focus of Rolan Wealth&rsquo;s Services.
            </strong>{' '}
            CFP® professionals&rsquo; role is limited to helping you understand
            financial concepts, explore potential strategies, and build
            financial awareness. They do not have discretionary authority over
            your financial decisions or assets.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        Rolan Wealth does not guarantee the accuracy, completeness, or
        timeliness of the information or coaching provided through its Services.
        Reliance on any information or coaching provided through Rolan Wealth is
        at your own risk.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">16. Indemnification</Title>

      <Text>
        To the fullest extent permitted by law, you agree to indemnify, defend,
        and hold harmless Rolan Wealth, its affiliates, partners, employees,
        agents, representatives, officers, directors, licensors, and suppliers
        (collectively, the &ldquo;Rolan Wealth Parties&rdquo;) from and against
        any and all claims, damages, losses, costs, and expenses (including
        reasonable attorneys&rsquo; fees) arising out of or related to:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>Your access to or use of the Services.</Text>
        </List.Item>

        <List.Item>
          <Text>Your violation of these Terms or any applicable law.</Text>
        </List.Item>

        <List.Item>
          <Text>
            Any content or materials you provide through the Services, including
            claims of infringement, misappropriation, or violation of
            intellectual property or other rights.
          </Text>
        </List.Item>

        <List.Item>
          <Text>Any disputes between you and a third party.</Text>
        </List.Item>

        <List.Item>
          <Text>
            Your financial decisions or actions taken in reliance on any
            content, coaching, AI-Powered Insight, or guidance obtained through
            the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Your decision to grant CFP® View Access and any consequences
            thereof.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        Rolan Wealth reserves the right to assume the exclusive defense and
        control of any matter otherwise subject to indemnification by you, and
        you agree to cooperate with our defense of such claims. You may not
        settle any claim without Rolan Wealth&rsquo;s prior written consent.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">17. Dispute Resolution and Arbitration</Title>

      <Title variant="h3">17.1 Informal Process First</Title>

      <Text>
        You agree that in the event of any dispute between you and Rolan Wealth,
        you will first contact us and make a good faith sustained effort to
        resolve the dispute informally before resorting to more formal means of
        resolution, including arbitration or court action. You can initiate this
        informal process by contacting us at support@rolanwealth.com. Rolan
        Wealth will make reasonable efforts to respond to your concerns and
        resolve the dispute informally within thirty (30) days of receiving your
        initial notice.
      </Text>

      <Title mt={20} variant="h3">
        17.2 Agreement to Arbitrate
      </Title>

      <Text>
        You and Rolan Wealth agree to resolve all disputes arising under these
        Terms or related to the Services through binding arbitration, except as
        noted below. Arbitration is more informal than a lawsuit in court and
        uses a neutral arbitrator instead of a judge or jury. The arbitration
        will be conducted under the rules of the American Arbitration
        Association (&ldquo;AAA&rdquo;) in accordance with its Consumer
        Arbitration Rules (the &ldquo;AAA Rules&rdquo;) as modified by this
        Section. The AAA Rules are available at www.adr.org or by calling the
        AAA at 1-800-778-7879.
      </Text>

      <Title mt={20} variant="h3">
        17.3 Scope of Arbitration
      </Title>

      <Text>
        This agreement to arbitrate covers any and all disputes, claims, or
        controversies between you and Rolan Wealth arising out of or related to
        these Terms or the use of our Services, whether based on contract, tort,
        statute, fraud, misrepresentation, or any other legal theory. The
        arbitrator shall have exclusive authority to resolve all disputes
        relating to the interpretation, applicability, enforceability, or
        formation of this arbitration agreement.
      </Text>

      <Title mt={20} variant="h3">
        17.4 Exceptions to Arbitration
      </Title>

      <Text>
        Notwithstanding the above, either party may bring an individual action
        in small claims court or seek injunctive relief in a court of competent
        jurisdiction for any actual or alleged infringement or misappropriation
        of a party&rsquo;s intellectual property rights.
      </Text>

      <Title mt={20} variant="h3">
        17.5 Arbitration Procedure
      </Title>

      <Text>
        The arbitration will be conducted by a single arbitrator selected by
        mutual agreement of the parties or, failing such agreement, appointed by
        the AAA in accordance with its rules. The arbitration may be conducted
        via telephone, video conference, or in person in Albany, New York,
        unless both parties agree to another location. If the value of the
        relief sought is $10,000 or less, you or Rolan Wealth may elect to have
        the arbitration conducted solely based on written submissions.
      </Text>

      <Title mt={20} variant="h3">
        17.6 Costs of Arbitration
      </Title>

      <Text>
        If Rolan Wealth elects arbitration, we will pay all AAA filing costs and
        administrative fees (excluding hearing fees). If you elect arbitration,
        filing costs and administrative fees will be paid in accordance with the
        AAA Rules or applicable law. However, if the value of the relief sought
        is $10,000 or less, and you request arbitration, Rolan Wealth will cover
        all filing, administrative, and arbitrator fees unless the arbitrator
        finds that the claim is frivolous or brought for an improper purpose, as
        defined under Federal Rule of Civil Procedure 11(b). Each party shall
        bear its own attorneys&rsquo; fees and costs unless otherwise required
        by law.
      </Text>

      <Title mt={20} variant="h3">
        17.7 Class Action Waiver
      </Title>

      <Text>
        <strong>
          YOU AND ROLAN WEALTH AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS,
          WHETHER IN ARBITRATION OR COURT, WILL BE CONDUCTED ONLY ON AN
          INDIVIDUAL BASIS AND NOT AS A CLASS, COLLECTIVE, OR REPRESENTATIVE
          ACTION. THE ARBITRATOR SHALL NOT CONSOLIDATE OR JOIN MORE THAN ONE
          PERSON&rsquo;S OR PARTY&rsquo;S CLAIMS, AND SHALL NOT OTHERWISE
          PRESIDE OVER ANY FORM OF A REPRESENTATIVE OR CLASS PROCEEDING. IF THIS
          SPECIFIC PROVISION IS FOUND TO BE UNENFORCEABLE, THEN THE ENTIRETY OF
          THIS ARBITRATION SECTION SHALL BE NULL AND VOID.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        17.8 Opt-Out Procedure
      </Title>

      <Text>
        You may opt out of this arbitration agreement by sending written notice
        of your decision to opt out to support@rolanwealth.com within thirty
        (30) days of first accepting these Terms. Your notice must include your
        name, address, and a clear statement that you wish to opt out of this
        arbitration agreement. Opting out will not affect any other parts of
        these Terms.
      </Text>

      <Title mt={20} variant="h3">
        17.9 Governing Law
      </Title>

      <Text>
        This arbitration agreement is governed by the Federal Arbitration Act.
        For any disputes not subject to arbitration, you and Rolan Wealth agree
        to submit to the exclusive jurisdiction of the state and federal courts
        located in Albany, New York.
      </Text>

      <Title mt={20} variant="h3">
        17.10 Survival
      </Title>

      <Text>
        This arbitration section shall survive termination of these Terms and
        the termination of your relationship with Rolan Wealth.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">18. Governing Law and Venue</Title>

      <Text>
        These Terms are governed by and construed in accordance with the laws of
        the State of New York, without regard to its conflict of laws
        principles. You agree that any legal action or proceeding not subject to
        arbitration must be brought exclusively in the federal or state courts
        located in Albany, New York. You consent to the personal jurisdiction of
        such courts and waive any objections to venue.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">19. Severability</Title>

      <Text>
        If any provision of these Terms is found to be unlawful, void, or for
        any reason unenforceable, then that provision shall be deemed severable
        from these Terms and shall not affect the validity and enforceability of
        any remaining provisions.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">20. Entire Agreement</Title>

      <Text>
        These Terms, along with our Privacy Policy, your Program Details as
        described in Section 7.3, and any additional terms or agreements
        expressly incorporated by reference, constitute the entire agreement
        between you and Rolan Wealth regarding the use of our Services and
        supersede all prior or contemporaneous communications, agreements, or
        understandings, whether oral or written, regarding the subject matter
        hereof.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">21. Contact Information</Title>

      <Text>
        If you have any questions about these Terms, please contact us at:
      </Text>

      <Text mt={8}>
        <strong>Rolan Wealth (CLZ Ventures LLC)</strong>
      </Text>

      <Text>Email: support@rolanwealth.com</Text>

      <Text>
        Address:{' '}
        <strong>54 State Street, Ste 804 #7100, Albany, New York 12207</strong>
      </Text>

      <Text mt={20}>
        CFP® and CERTIFIED FINANCIAL PLANNER&trade; are certification marks
        owned by Certified Financial Planner Board of Standards, Inc.
      </Text>
    </Box>
  )
}

Component.displayName = 'TermsConditions'
